/**
 * VoucherDetailView — one voucher rendered the way Tally's voucher screen
 * renders it: header details, then every ledger line in Tally's own order with
 * its debit/credit split, the party line marked, bill-wise allocations nested
 * under the line they belong to, and the inventory grid when there is one.
 *
 * All of it comes from GET /db/companies/:id/voucher/:guid, which reads only
 * tables the sync already writes.
 */

import React from 'react';
import './VoucherTable.css';
import './DrillDown.css';

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const fmtTotal = (n) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const fmtQty = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 }).format(Number(n) || 0);

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Tally serialises an absent bill reference as this literal sentence rather
 * than an empty element, and the sync stores it verbatim — it is the value of
 * billName on all 39,829 bill_allocations rows in the database. Printing it
 * would present a placeholder as if it were a bill number, so the allocation
 * is shown by its type and amount instead.
 */
const NO_BILL_REF = 'This field is empty in Tally';

const billLabel = (b) => {
  const ref = b.billName && b.billName !== NO_BILL_REF ? b.billName : null;
  const parts = [ref || '(no bill reference)'];
  if (b.billType) parts.push(b.billType);
  return parts.join(' · ');
};

const Meta = ({ label, value }) =>
  value === null || value === undefined || value === '' ? null : (
    <div className="dd-meta-item">
      <span className="dd-meta-label">{label}</span>
      <span className="dd-meta-value">{value}</span>
    </div>
  );

const VoucherDetailView = ({ data, loading, error }) => {
  if (loading) return <div className="vt-state vt-loading">Loading voucher…</div>;
  if (error) return <div className="vt-state vt-error">Failed to load voucher: {error}</div>;
  if (!data) return <div className="vt-state vt-empty">No voucher selected.</div>;

  const { voucher: v, ledgerEntries = [], inventoryEntries = [], totals, emptyReason } = data;

  return (
    <>
      {v.isCancelled && (
        <div className="dd-notice">
          This voucher is <strong>cancelled</strong> in Tally. Cancelled vouchers carry no ledger
          postings, which is why no lines are shown below.
        </div>
      )}
      {v.isNonAccounting && !v.isCancelled && (
        <div className="dd-notice dd-notice-plain">
          <strong>{v.voucherTypeName}</strong> is a non-accounting voucher. Tally records the
          document but does not post it to the books, so these lines are excluded from the Trial
          Balance and Ledger balances.
        </div>
      )}

      <div className="dd-meta">
        <Meta label="Voucher No." value={v.voucherNumber} />
        <Meta label="Date" value={fmtDate(v.date)} />
        <Meta label="Voucher Type" value={v.voucherTypeName} />
        <Meta label="Class" value={v.voucherClass} />
        <Meta label="Party" value={v.partyLedgerName || v.partyLedgerMasterName} />
        <Meta label="Party Group" value={v.partyLedgerGroup} />
        <Meta label="Party GSTIN" value={v.partyGSTIN || v.partyLedgerGSTIN} />
        <Meta label="Place of Supply" value={v.placeOfSupply} />
        <Meta label="Reference" value={v.reference} />
        <Meta label="Reference Date" value={v.referenceDate ? fmtDate(v.referenceDate) : null} />
        <Meta label="e-Way Bill" value={v.eWayBillNo} />
        <Meta label="Company" value={v.companyName} />
        <Meta label="Voucher Amount" value={`₹ ${fmtTotal(v.amount)}`} />
      </div>

      {v.narration ? (
        <div className="dd-notice dd-notice-plain">
          <strong>Narration:</strong> {v.narration}
        </div>
      ) : null}

      <h3 className="dd-section-title">
        Ledger Entries
        {totals?.lineCount > 0 && (
          <span className={`dd-tag ${totals.balanced ? 'dd-tag-ok' : 'dd-tag-warn'}`}>
            {totals.balanced ? 'Balanced' : `Out by ${fmtTotal(totals.difference)}`}
          </span>
        )}
      </h3>

      {ledgerEntries.length === 0 ? (
        <div className="vt-state vt-empty">{emptyReason || 'No ledger entries for this voucher.'}</div>
      ) : (
        <div className="voucher-table-wrap">
          <table className="voucher-table">
            <thead>
              <tr>
                <th>Particulars</th>
                <th>Group</th>
                <th className="vt-amount-col">Debit</th>
                <th className="vt-amount-col">Credit</th>
              </tr>
            </thead>
            <tbody>
              {ledgerEntries.map((l) => (
                <tr key={l.id}>
                  <td className="vt-party" title={l.ledgerName}>
                    {l.ledgerName || '—'}
                    {l.isParty && <span className="dd-tag dd-tag-party">Party</span>}
                    {l.billAllocations?.length > 0 && (
                      <ul className="dd-bills">
                        {l.billAllocations.map((b) => (
                          <li key={b.id}>
                            {/* Tally signs bill amounts (negative = debit); the
                                line it hangs off already shows the side, so
                                only the magnitude is meaningful here. */}
                            {billLabel(b)} · ₹ {fmtTotal(Math.abs(b.amount))}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="vt-narration" title={l.groupName}>{l.groupName || '—'}</td>
                  <td className="vt-amount vt-out">{fmt(l.debit)}</td>
                  <td className="vt-amount vt-in">{fmt(l.credit)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="dd-boundary-row">
                <td colSpan={2}>Total</td>
                <td className="vt-amount vt-out">{fmtTotal(totals.debit)}</td>
                <td className="vt-amount vt-in">{fmtTotal(totals.credit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {inventoryEntries.length > 0 && (
        <>
          <h3 className="dd-section-title">Inventory Entries</h3>
          <div className="voucher-table-wrap">
            <table className="voucher-table">
              <thead>
                <tr>
                  <th>Stock Item</th>
                  <th>Batch</th>
                  <th className="vt-amount-col">Quantity</th>
                  <th className="vt-amount-col">Rate</th>
                  <th className="vt-amount-col">Discount</th>
                  <th className="vt-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inventoryEntries.map((i) => (
                  <tr key={i.id}>
                    <td className="vt-party" title={i.stockItemName}>{i.stockItemName || '—'}</td>
                    <td className="vt-narration">{i.batch || '—'}</td>
                    <td className="dd-running">
                      {fmtQty(i.quantity)}
                      {i.unit ? <span className="dd-side">{i.unit}</span> : null}
                    </td>
                    <td className="dd-running">{fmtTotal(i.rate)}</td>
                    <td className="dd-running">{i.discount ? fmtTotal(i.discount) : '—'}</td>
                    <td className="vt-amount">{fmtTotal(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default VoucherDetailView;
